import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Activity, CalendarClock, ClipboardList, Eye, FileText, Layers, LayoutDashboard, RefreshCw, SlidersHorizontal } from 'lucide-react';
import apiService from '../../services/apiService';
import ForecastNotificationsTray from './ForecastNotificationsTray';
import {
  FORECAST_USERS,
  STATUS_LABELS,
  STATUS_TONES,
  formatMillions,
  formatPct,
  getActiveForecastUser,
  setActiveForecastUser
} from './forecastUsers';

const ACTION_LABELS = {
  not_started: 'Start',
  in_progress: 'Resume',
  returned: 'Address comments',
  submitted: 'View (locked)',
  approved: 'View (approved)',
  loaded: 'View (loaded)'
};

const canManageMacroScenarios = (roleContext) => {
  const roles = (roleContext?.roles || []).map((role) => String(role).toLowerCase());
  const permissions = (roleContext?.permissions || []).map((permission) => String(permission).toLowerCase());
  return roles.includes('central_fpa') || permissions.includes('macro:manage');
};

const snapshotDelta = (submission) => {
  const fy1 = submission?.modelSnapshot?.annualResults?.[0];
  if (!fy1) return null;
  return { delta: fy1.delta, pct: fy1.variancePct, year: fy1.year };
};

const ForecastCycleHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [routeNotice, setRouteNotice] = useState(location.state?.routeNotice || null);
  const [activeUser, setActiveUserState] = useState(getActiveForecastUser());
  const [selectedIds, setSelectedIds] = useState([]);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleContext, setRoleContext] = useState(null);
  const [returnComments, setReturnComments] = useState({}); // submissionId -> comment (B1)

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Server-side coverage scoping: liaisons/managers only receive the units
      // mapped to them in forecast_org_map; central/executive receive all units.
      const result = await apiService.fetchForecastSubmissions('FY2027_BUDGET', activeUser.name);
      setData(result);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Failed to load forecast cycle');
    } finally {
      setLoading(false);
    }
  };

  // Re-fetch whenever the acting-as identity changes (switcher lives on this page).
  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeUser.name]);

  useEffect(() => {
    if (!data) return undefined;
    let cancelled = false;
    const cycleCode = data?.cycle?.code || data?.cycle?.cycle_code || 'FY2027_BUDGET';
    apiService.fetchForecastRoleContext(activeUser.name, cycleCode)
      .then((ctx) => { if (!cancelled) setRoleContext(ctx); })
      .catch(() => { if (!cancelled) setRoleContext(null); });
    return () => { cancelled = true; };
  }, [data, activeUser.name]);

  // B1: pull the waiting-on-me queue to surface each returned unit's comment
  // inline (the queue carries returnedComment keyed by submissionId).
  useEffect(() => {
    let cancelled = false;
    const cycleCode = data?.cycle?.code || data?.cycle?.cycle_code || 'FY2027_BUDGET';
    apiService.fetchForecastWaitingQueue({ cycleCode, actor: activeUser.name })
      .then((result) => {
        if (cancelled) return;
        const lookup = {};
        (result?.items || []).forEach((item) => {
          if (item.returnedComment) lookup[item.submissionId] = item.returnedComment;
        });
        setReturnComments(lookup);
      })
      .catch(() => { if (!cancelled) setReturnComments({}); });
    return () => { cancelled = true; };
  }, [data, activeUser.name]);

  const handleUserChange = (event) => {
    const user = FORECAST_USERS.find((candidate) => candidate.name === event.target.value);
    if (user) {
      setActiveForecastUser(user.name);
      setActiveUserState(user);
    }
  };

  const cycle = data?.cycle;
  const submissions = useMemo(() => data?.submissions || [], [data]);
  const counts = data?.statusCounts || {};
  const scope = data?.scope || null;
  const coverage = data?.coverage;
  const isScoped = coverage
    ? coverage.scoped
    : activeUser.role === 'liaison' || activeUser.role === 'manager';
  const total = scope?.total ?? submissions.length;
  const submittedOrBeyond = scope?.submittedOrBeyond ?? ((counts.submitted || 0) + (counts.approved || 0) + (counts.loaded || 0));
  const approvedOrLoaded = scope?.approvedOrLoaded ?? ((counts.approved || 0) + (counts.loaded || 0));
  const returned = scope?.returned ?? (counts.returned || 0);
  const canOpenMacroScenarios = canManageMacroScenarios(roleContext);

  const dueInDays = useMemo(() => {
    if (!cycle?.due_date && !cycle?.dueDate) return null;
    const due = new Date(cycle.due_date || cycle.dueDate);
    return Math.ceil((due.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  }, [cycle]);

  const orderedSubmissions = useMemo(() => {
    const priority = { returned: 0, in_progress: 1, not_started: 2, submitted: 3, approved: 4, loaded: 5 };
    return [...submissions].sort((a, b) => {
      const mineA = a.assignedTo === activeUser.name ? 0 : 1;
      const mineB = b.assignedTo === activeUser.name ? 0 : 1;
      if (mineA !== mineB) return mineA - mineB;
      if (priority[a.status] !== priority[b.status]) return priority[a.status] - priority[b.status];
      return `${a.division}${a.market}`.localeCompare(`${b.division}${b.market}`);
    });
  }, [submissions, activeUser]);

  return (
    <div className="forecast-module forecast-home">
      {routeNotice && (
        <div className="forecast-inline-note" role="status" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, margin: '8px 0' }}>
          <span>{routeNotice}</span>
          <button type="button" className="forecast-chip-button" onClick={() => setRouteNotice(null)}>Dismiss</button>
        </div>
      )}
      <section className="forecast-command">
        <div>
          <div className="forecast-kicker">
            <Activity size={14} />
            Forecast Console / Cycle home
          </div>
          <h1>{cycle?.name || 'Forecast cycle'}</h1>
          <p>
            Pick your business unit, review the baseline, work the assumptions, and submit a clean
            package to FP&A. Managers review from the console; executives read the summary.
          </p>
        </div>

        <div className="forecast-command-grid">
          <label>
            Acting as
            <select value={activeUser.name} onChange={handleUserChange}>
              {FORECAST_USERS.map((user) => (
                <option value={user.name} key={user.name}>
                  {user.label}
                </option>
              ))}
            </select>
            {roleContext && (
              <span className="forecast-health-chip neutral" style={{ marginTop: 6, display: 'inline-block' }}>
                Server role: {(roleContext.roles || []).join(', ') || '—'} · {(roleContext.coverage || []).length} units
              </span>
            )}
          </label>
          <div className="forecast-home-links">
            {activeUser.role !== 'liaison' && (
              <Link className="forecast-home-link" to="/forecasting/review">
                <LayoutDashboard size={14} /> Review console
              </Link>
            )}
            {activeUser.role !== 'liaison' && (
              <Link className="forecast-home-link" to="/forecasting/statements">
                <FileText size={14} /> Statement review
              </Link>
            )}
            <Link className="forecast-home-link" to="/forecasting/exec">
              <Eye size={14} /> Executive view
            </Link>
            {canOpenMacroScenarios && (
              <Link className="forecast-home-link" to="/forecasting/macro">
                <SlidersHorizontal size={14} /> Macro scenarios
              </Link>
            )}
            <Link className="forecast-home-link" to="/forecasting/projects">
              <ClipboardList size={14} /> Corporate projects
            </Link>
          </div>
        </div>
      </section>

      <section className="forecast-status-strip">
        <div>
          <span>Due date</span>
          <strong>
            <CalendarClock size={13} style={{ marginRight: 4, verticalAlign: '-2px' }} />
            {cycle ? `${(cycle.due_date || cycle.dueDate || '').slice(0, 10)}${dueInDays !== null ? ` · ${dueInDays}d left` : ''}` : '—'}
          </strong>
        </div>
        <div>
          <span>Baseline / prior cycle</span>
          <strong>{cycle ? `${cycle.baseline_version || cycle.baselineVersion} vs ${cycle.prior_version || cycle.priorVersion}` : '—'}</strong>
        </div>
        <div>
          <span>Cycle progress</span>
          <strong>{total ? `${submittedOrBeyond} of ${total} submitted · ${approvedOrLoaded} approved/loaded · ${returned} returned` : '—'}</strong>
        </div>
        <div>
          <span>Status</span>
          <strong>{cycle?.status === 'open' ? 'Open for input' : cycle?.status || '—'}</strong>
        </div>
        <div>
          <span>Alerts</span>
          <strong>
            <ForecastNotificationsTray
              actor={activeUser.name}
              cycleCode={cycle?.code || cycle?.cycle_code || 'FY2027_BUDGET'}
            />
          </strong>
        </div>
      </section>

      {error && (
        <div className="forecast-panel forecast-home-error">
          <p>{error}</p>
          <button type="button" onClick={loadData}>
            <RefreshCw size={14} /> Retry
          </button>
        </div>
      )}

      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">CYCLE ● Business unit packages</span>
          <h2>
            <ClipboardList size={16} style={{ marginRight: 6, verticalAlign: '-2px' }} />
            {isScoped ? 'Your covered business units' : 'All business unit packages'}
          </h2>
        </div>

        <div className="forecast-submit-chips" style={{ marginBottom: 10 }}>
          {coverage && (
            <span className="forecast-health-chip neutral">
              {coverage.scoped
                ? `My coverage · ${coverage.count} unit${coverage.count === 1 ? '' : 's'}`
                : 'All units'}
            </span>
          )}
          <button
            type="button"
            className="forecast-primary-button compact"
            disabled={selectedIds.length < 2}
            onClick={() => navigate(`/forecasting/input/aggregate?units=${selectedIds.join(',')}`)}
          >
            <Layers size={13} /> Forecast in aggregate & allocate
            {selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}
          </button>
          {selectedIds.length === 1 && (
            <span className="forecast-health-chip neutral">Select at least two units to aggregate</span>
          )}
          {selectedIds.length > 1 && (
            <button type="button" className="forecast-secondary-button compact" onClick={() => setSelectedIds([])}>
              Clear selection
            </button>
          )}
        </div>

        {loading ? (
          <p className="forecast-home-loading">Loading cycle…</p>
        ) : orderedSubmissions.length === 0 ? (
          <div className="forecast-home-empty" style={{ padding: '14px 2px' }}>
            <p className="forecast-home-loading" style={{ margin: 0 }}>
              {coverage?.scoped
                ? `No business unit packages are mapped to ${coverage.actingAs || activeUser.name} in this cycle — contact FP&A to update coverage.`
                : 'No business unit packages found in this cycle.'}
            </p>
          </div>
        ) : (
          <div className="forecast-home-table-wrap">
            <table className="forecast-home-table">
              <thead>
                <tr>
                  <th />
                  <th>Business unit</th>
                  <th>Status</th>
                  <th>Assigned</th>
                  <th>FY1 NI Δ vs baseline</th>
                  <th>Last saved</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {orderedSubmissions.map((submission) => {
                  const delta = snapshotDelta(submission);
                  const mine = submission.assignedTo === activeUser.name;
                  return (
                    <tr key={submission.id} className={mine ? 'mine' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          className="forecast-unit-check"
                          checked={selectedIds.includes(submission.id)}
                          disabled={['approved', 'loaded'].includes(submission.status)}
                          onChange={(event) =>
                            setSelectedIds((previous) =>
                              event.target.checked
                                ? [...previous, submission.id]
                                : previous.filter((id) => id !== submission.id)
                            )
                          }
                        />
                      </td>
                      <td>
                        <strong>{submission.division}</strong>
                        <span className="forecast-home-market"> · {submission.market}</span>
                        {submission.status === 'returned' && returnComments[submission.id] && (
                          <div className="forecast-queue-returned">↩ {returnComments[submission.id]}</div>
                        )}
                      </td>
                      <td>
                        <span className={`forecast-status-chip ${STATUS_TONES[submission.status]}`}>
                          {STATUS_LABELS[submission.status]}
                        </span>
                      </td>
                      <td>{submission.assignedTo === 'Unassigned' ? '—' : submission.assignedTo}</td>
                      <td>
                        {delta ? (
                          <span className={delta.delta >= 0 ? 'pos' : 'neg'}>
                            {formatMillions(delta.delta)} ({formatPct(delta.pct)})
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td>{submission.updatedAt ? submission.updatedAt.slice(0, 16).replace('T', ' ') : '—'}</td>
                      <td>
                        <button
                          type="button"
                          className="forecast-home-action"
                          onClick={() => navigate(`/forecasting/input/${submission.id}`)}
                        >
                          {ACTION_LABELS[submission.status] || 'Open'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="forecast-panel forecast-home-reference">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">REF ● Reference</span>
        </div>
        <p>
          Baseline methodology: GL actuals through latest close · Macro set: central base + shock
          scenarios · Prior cycle for comparison: {cycle?.prior_version || cycle?.priorVersion || '—'} ·
          Budget anchor: {cycle?.budget_version || cycle?.budgetVersion || '—'}.
        </p>
      </section>
    </div>
  );
};

export default ForecastCycleHome;
